// src/components/FormBuilder.jsx
import React, { useState } from 'react';
import config from '../config';
import './FormBuilder.css';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'image', label: 'Images' },
];

const FormBuilder = ({ onTemplateCreated, onBack }) => {
  const [templateName, setTemplateName] = useState('');
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addSection = () => {
    const newSection = {
      id: Date.now().toString(),
      name: `Section ${sections.length + 1}`,
      fields: []
    };
    setSections([...sections, newSection]);
    setError('');
  };

  const updateSectionName = (sectionId, name) => {
    setSections(sections.map(section =>
      section.id === sectionId ? { ...section, name } : section
    ));
  };

  const addField = (sectionId) => {
    const newField = {
      id: Date.now().toString(),
      label: 'New Field',
      field_type: 'text',
      required: false,
      validation_criteria: {},
      options: []
    };

    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, fields: [...section.fields, newField] }
        : section
    ));
  };

  const updateField = (sectionId, fieldId, updates) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? {
          ...section,
          fields: section.fields.map(field =>
            field.id === fieldId ? { ...field, ...updates } : field
          )
        }
        : section
    ));
  };

  const removeSection = (sectionId) => {
    setSections(sections.filter(section => section.id !== sectionId));
  };

  const removeField = (sectionId, fieldId) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? {
          ...section,
          fields: section.fields.filter(field => field.id !== fieldId)
        }
        : section
    ));
  };

  const validateTemplate = () => {
    if (!templateName.trim()) {
      setError('Please enter a template name');
      return false;
    }

    if (sections.length === 0) {
      setError('Please add at least one section');
      return false;
    }

    // Check if all sections have names
    const sectionsWithoutNames = sections.filter(s => !s.name.trim());
    if (sectionsWithoutNames.length > 0) {
      setError('All sections must have names');
      return false;
    }

    // Check if all sections have at least one field
    const emptySections = sections.filter(section => section.fields.length === 0);
    if (emptySections.length > 0) {
      setError('All sections must have at least one field');
      return false;
    }

    // Check if all fields have labels
    for (const section of sections) {
      const fieldsWithoutLabels = section.fields.filter(f => !f.label.trim());
      if (fieldsWithoutLabels.length > 0) {
        setError('All fields must have labels');
        return false;
      }
    }

    // Check if select/radio/checkbox fields have options
    for (const section of sections) {
      for (const field of section.fields) {
        if (['select', 'radio', 'checkbox'].includes(field.field_type) &&
          (!field.options || field.options.length === 0)) {
          setError(`Field "${field.label}" must have at least one option`);
          return false;
        }
      }
    }

    return true;
  };

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const saveTemplate = async () => {
    // Clear previous messages
    setError('');
    setSuccessMessage('');

    // Validate template
    if (!validateTemplate()) {
      return;
    }

    setIsLoading(true);

    try {
      // Prepare the data in the format expected by Django
      const templateData = {
        name: templateName,
        sections_data: sections.map(section => ({
          name: section.name,
          fields: section.fields.map(field => {
            // Base field data
            const fieldData = {
              label: field.label,
              field_type: field.field_type,
              required: field.required,
              validation_criteria: field.validation_criteria || {},
            };

            // Add options for select/radio/checkbox fields
            if (['select', 'radio', 'checkbox'].includes(field.field_type)) {
              fieldData.options = field.options || [];
            }

            return fieldData;
          })
        }))
      };

      // console.log('Sending data:', templateData); // Debug log

      // Get CSRF token
      const csrftoken = getCookie('csrftoken');

      const response = await fetch(`${config.API_BASE_URL}/api/form-templates/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify(templateData)
      });

      // console.log('after api call', response); // Debug log

      // Parse response
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        responseData = { message: 'Invalid JSON response' };
      }

      if (!response.ok) {
        // Handle different error status codes
        if (response.status === 401) {
          throw new Error('You need to be logged in to create templates');
        } else if (response.status === 403) {
          throw new Error('CSRF verification failed. Please refresh the page and try again.');
        } else if (response.status === 400) {
          // Validation errors
          const errorMessages = [];
          if (responseData) {
            Object.keys(responseData).forEach(key => {
              if (Array.isArray(responseData[key])) {
                errorMessages.push(`${key}: ${responseData[key].join(', ')}`);
              } else {
                errorMessages.push(`${key}: ${responseData[key]}`);
              }
            });
          }
          throw new Error(errorMessages.length > 0
            ? errorMessages.join('\n')
            : 'Please check your form data and try again');
        } else {
          throw new Error(responseData.message || `Server error: ${response.status}`);
        }
      }

      // Success!
      // console.log('Template saved:', responseData);
      setSuccessMessage('Template saved successfully!');

      // Reset form
      setTemplateName('');
      setSections([]);

      // Notify parent
      if (onTemplateCreated) {
        onTemplateCreated();
      }

    } catch (error) {
      // console.error('Error saving template:', error);
      setError(error.message || 'Failed to save template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="form-builder-container">
      <button onClick={onBack} className="back-button" disabled={isSubmitting}>
        <span className="back-icon">←</span>
        Back to Templates
      </button>
      <h1 className="builder-title">Create New Form Template</h1>

      {/* Error Message */}
      {error && (
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          <div className="error-content">
            <strong>Error:</strong>
            <p>{error}</p>
          </div>
          <button className="error-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="success-alert">
          <span className="success-icon">✓</span>
          <span>{successMessage}</span>
          <button className="success-close" onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      {/* Template Name Input */}
      <div className="template-name-section">
        <label htmlFor="templateName" className="field-label">
          Template Name <span className="required-star">*</span>
        </label>
        <input
          id="templateName"
          type="text"
          placeholder="Enter template name (e.g., Customer Feedback Form)"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="template-name-input"
          disabled={isLoading}
        />
      </div>

      {/* Action Buttons */}
      <div className="builder-actions">
        <button
          onClick={addSection}
          className="btn-add-section"
          disabled={isLoading}
        >
          <span className="btn-icon">+</span>
          Add Section
        </button>

        <button
          onClick={saveTemplate}
          disabled={isLoading || !templateName || sections.length === 0}
          className="btn-save-template"
        >
          {isLoading ? (
            <>
              <span className="loading-spinner-small"></span>
              Saving...
            </>
          ) : (
            <>
              <span className="btn-icon">💾</span>
              Save Template
            </>
          )}
        </button>
      </div>

      {/* Sections */}
      {sections.length === 0 ? (
        <div className="empty-state-builder">
          <div className="empty-icon">📋</div>
          <h3>No sections added yet</h3>
          <p>Click "Add Section" to start building your form</p>
        </div>
      ) : (
        sections.map((section, sectionIndex) => (
          <div key={section.id} className="section-card-builder">
            <div className="section-header-builder">
              <div className="section-title-input">
                <input
                  type="text"
                  value={section.name}
                  onChange={(e) => updateSectionName(section.id, e.target.value)}
                  className="section-name-input"
                  placeholder="Section name (e.g., Personal Information)"
                  disabled={isLoading}
                />
              </div>

              <button
                onClick={() => removeSection(section.id)}
                className="btn-remove-section"
                disabled={isLoading}
                title="Remove Section"
              >
                🗑️
              </button>
            </div>

            <button
              onClick={() => addField(section.id)}
              className="btn-add-field"
              disabled={isLoading}
            >
              <span className="btn-icon">+</span>
              Add Field
            </button>

            {section.fields.length === 0 ? (
              <div className="empty-fields-message">
                No fields in this section. Click "Add Field" to get started.
              </div>
            ) : (
              section.fields.map((field) => (
                <div key={field.id} className="field-card-builder">
                  <div className="field-header">
                    <span className="field-type-badge">
                      {FIELD_TYPES.find(t => t.value === field.field_type)?.label || 'Text'}
                    </span>
                    <button
                      onClick={() => removeField(section.id, field.id)}
                      className="btn-remove-field"
                      disabled={isLoading}
                      title="Remove Field"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="field-inputs">
                    <div className="field-label-input">
                      <label className="field-sub-label">Field Label *</label>
                      <input
                        type="text"
                        placeholder="e.g., Full Name"
                        value={field.label}
                        onChange={(e) => updateField(section.id, field.id, { label: e.target.value })}
                        className="text-input"
                        disabled={isLoading}
                      />
                    </div>

                    <div className="field-type-select">
                      <label className="field-sub-label">Field Type *</label>
                      <select
                        value={field.field_type}
                        onChange={(e) => updateField(section.id, field.id, { field_type: e.target.value })}
                        className="select-input"
                        disabled={isLoading}
                      >
                        {FIELD_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="field-required">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(section.id, field.id, { required: e.target.checked })}
                        disabled={isLoading}
                      />
                      This field is required
                    </label>
                  </div>

                  {['select', 'radio', 'checkbox'].includes(field.field_type) && (
                    <div className="field-options">
                      <label className="field-sub-label">Options (one per line) *</label>
                      <textarea
                        value={field.options?.join('\n') || ''}
                        onChange={(e) => updateField(section.id, field.id, {
                          options: e.target.value.split('\n').filter(opt => opt.trim())
                        })}
                        className="options-textarea"
                        rows={4}
                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                        disabled={isLoading}
                      />
                      <p className="options-help">Enter each option on a new line</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default FormBuilder;